import { EventEmitter } from 'events';
import { getHardwareService } from './HardwareIntegrationService';
import { getKeyboardWedgeScanner } from './KeyboardWedgeScanner';
import store from '@/store';
import Vue from 'vue';

export class ScannerEventService extends EventEmitter {
  private hardwareService;
  private keyboardWedgeScanner;
  private isListening: boolean = false;
  private lastScanTime: number = 0;
  private scanDebounceMs: number = 1000;
  private scanHistory: Map<string, number> = new Map(); // Track recent scans to prevent duplicates

  constructor() {
    super();
    this.hardwareService = getHardwareService(true); // Force UHB mode
    this.keyboardWedgeScanner = getKeyboardWedgeScanner(); // CRITICAL FIX: Add keyboard wedge scanner
  }

  /**
   * Check if scanner debug logging should be enabled
   * Only logs when in development mode AND hardware widget is visible
   */
  private shouldLog(): boolean {
    if (process.env.NODE_ENV !== 'development') {
      return false;
    }

    // Check if hardware widget is visible in localStorage
    try {
      const widgetVisible = localStorage.getItem('hardware_widget_visible');
      return widgetVisible === 'true';
    } catch (error) {
      // If localStorage is not available, default to true in development
      return true;
    }
  }

  /**
   * Conditional console.log - only logs when shouldLog() returns true
   */
  private debugLog(...args: any[]): void {
    if (this.shouldLog()) {
      console.log(...args);
    }
  }

  /**
   * Conditional console.warn - only logs when shouldLog() returns true
   */
  private debugWarn(...args: any[]): void {
    if (this.shouldLog()) {
      console.warn(...args);
    }
  }

  /**
   * Conditional console.error - only logs when shouldLog() returns true
   */
  private debugError(...args: any[]): void {
    if (this.shouldLog()) {
      console.error(...args);
    }
  }

  async initialize() {
    if (this.isListening) return;
    
    this.debugLog('[ScannerEventService] Initializing global scanner service...');
    
    // Initialize hardware service
    await this.hardwareService.initialize();
    
    // Listen for primary scan events from hardware service (UHB/WebSocket scans)
    const uhbHandler = (data: any) => {
      console.log('[ScannerEventService] Scan received via UHB WebSocket path');
      this.handleScan(data);
    };
    this.hardwareService.on('scan', uhbHandler);
    this.hardwareService.on('scanner.barcode', uhbHandler);
    this.hardwareService.on('barcode', uhbHandler);

    // Listen for keyboard wedge scanner events (focus-based scans)
    const kbwHandler = (data: any) => {
      console.log('[ScannerEventService] Scan received via KeyboardWedge (requires focus)');
      this.handleScan(data);
    };
    this.keyboardWedgeScanner.on('scan', kbwHandler);

    // Log all events for debugging
    this.hardwareService.on('*', (eventName: string, data: any) => {
      if (eventName.includes('scan') || eventName.includes('barcode')) {
        this.debugLog('[ScannerEventService] Received HardwareService event:', eventName, data);
      }
    });
    
    this.isListening = true;
    this.debugLog('[ScannerEventService] Scanner service initialized and listening globally');
  }

  private handleScan(data: any) {
    this.debugLog('[ScannerEventService] handleScan called with:', data);

    // Immediate duplicate protection - prevent rapid-fire duplicate calls
    const now = Date.now();
    if (now - this.lastScanTime < 100) { // 100ms immediate duplicate protection
      this.debugLog('[ScannerEventService] Immediate duplicate call ignored (< 100ms)');
      return;
    }

    // Update lastScanTime immediately to prevent subsequent duplicates
    this.lastScanTime = now;

    // Filter out weight data from scales to prevent it from being treated as scan data
    if (data && data.weight) {
      this.debugLog('[ScannerEventService] Ignoring weight data - this is not scan data:', data);
      return;
    }

    // Filter out weight readings that look like numbers with decimal points
    if (data && typeof data === 'object' && data.scaleId) {
      this.debugLog('[ScannerEventService] Ignoring scale reading data:', data);
      return;
    }

    // Handle global keyboard scanner events from UHB
    if (data && data.type === 'keyboard-wedge-global') {
      console.log('[ScannerEventService] Global keyboard scan from UHB (focus-independent)');

      // UHB sends the barcode in data.value, not data.data
      const scanData = data.value || data.data || '';

      if (!scanData) {
        this.debugLog('[ScannerEventService] Empty global scan data, ignoring');
        return;
      }

      // Handle package QR codes with embedded data (new UHB format)
      if (data.scanType === 'package-qr' && data.packageData) {
        this.debugLog('[ScannerEventService] UHB PACKAGE QR DETECTED with embedded data:', data.packageData);
        this.handlePackageQRScan(data.packageData.id || 1, data.packageData, scanData);
        return;
      }

      // Use standard barcode processing: check for package QR first, then pick ticket
      const packageQRData = this.extractPackageQRData(scanData);
      if (packageQRData) {
        this.handlePackageQRScan(packageQRData.id, packageQRData.data, scanData);
        return;
      }

      const pickTicketId = data.pickTicketId || this.extractPickTicketId(scanData);
      if (pickTicketId) {
        this.handlePickTicketScan(pickTicketId, scanData);
      } else {
        // Generic barcode from global scanner
        this.emit('barcode-scanned', {
          data: scanData,
          timestamp: data.timestamp || Date.now(),
          source: 'global-keyboard'
        });

        // const scanString = typeof scanData === 'string' ? scanData : String(scanData || '');
        // const preview = scanString.length > 20 ? scanString.substring(0, 20) + '...' : scanString;
        // Vue.prototype.$toast?.info(`Scanned (Global): ${preview}`);
      }
      return;
    }
    
    // Extract scan data from various event formats (existing logic)
    const scanData = typeof data === 'string' ? data :
                    data.data || 
                    data.barcode?.value || 
                    data.barcode ||
                    data.value ||
                    data.code ||
                    '';
    
    if (!scanData) {
      this.debugLog('[ScannerEventService] Empty scan data received');
      return;
    }
    
    // Filter out numeric values that look like weight readings (e.g., "3.2", "12.5")
    const scanString = typeof scanData === 'string' ? scanData : String(scanData || '');
    if (/^\d+\.?\d*$/.test(scanString.trim()) && parseFloat(scanString) < 100) {
      this.debugLog('[ScannerEventService] Ignoring numeric value that appears to be a weight reading:', scanString);
      return;
    }

    this.debugLog('[ScannerEventService] Extracted scan data:', scanData);

    // Debounce duplicate scans (reuse 'now' from above)
    const lastScanOfThisData = this.scanHistory.get(scanData) || 0;
    
    if (now - lastScanOfThisData < this.scanDebounceMs) {
      this.debugLog('[ScannerEventService] Duplicate scan ignored:', scanData);
      return;
    }
    
    // Update scan history
    this.scanHistory.set(scanData, now);
    // lastScanTime already updated at start of method
    
    // Clean old entries from history (older than 5 seconds)
    for (const [key, time] of this.scanHistory.entries()) {
      if (now - time > 5000) {
        this.scanHistory.delete(key);
      }
    }
    
    this.debugLog('[ScannerEventService] Processing scan:', scanData);

    // Check if this is a package QR code first (more specific than pick tickets)
    const packageQRData = this.extractPackageQRData(scanData);

    if (packageQRData) {
      this.handlePackageQRScan(packageQRData.id, packageQRData.data, scanData);
      return;
    }

    // Check if this is a pick ticket barcode
    const pickTicketId = this.extractPickTicketId(scanData);

    if (pickTicketId) {
      this.handlePickTicketScan(pickTicketId, scanData);
    } else {
      // Emit generic scan event for other barcode types
      this.emit('barcode-scanned', {
        data: scanData,
        timestamp: now,
        source: 'hid-device'
      });

      // Show notification for unrecognized barcode
      // const scanString = typeof scanData === 'string' ? scanData : String(scanData || '');
      // const preview = scanString.length > 20 ? scanString.substring(0, 20) + '...' : scanString;
      // Vue.prototype.$toast?.info(`Scanned: ${preview}`);
    }
  }

  private extractPackageQRData(scanData: any): { id: number, data: any } | null {
    this.debugLog('[ScannerEventService] extractPackageQRData called with:', scanData);

    // Ensure scanData is a string
    const scanString = typeof scanData === 'string' ? scanData :
                      (scanData && typeof scanData.toString === 'function' ? scanData.toString() : '');

    this.debugLog('[ScannerEventService] scanString after conversion:', scanString);

    if (!scanString) {
      this.debugLog('[ScannerEventService] scanString is empty, returning null');
      return null;
    }

    // Remove any asterisks (CODE128 formatting) and whitespace
    const cleaned = scanString.replace(/\*/g, '').trim();
    this.debugLog('[ScannerEventService] cleaned string:', cleaned);

    // First try new scanner-friendly format: PKG{version}L{length}W{width}H{height}T{timestamp}C{checksum}
    if (cleaned.startsWith('PKG')) {
      this.debugLog('[ScannerEventService] *** SCANNER-FRIENDLY PACKAGE QR DETECTED ***');
      const packageData = this.parseScannerFriendlyQR(cleaned);
      if (packageData) {
        this.debugLog('[ScannerEventService] Scanner-friendly QR validation SUCCESS, returning data');
        return packageData;
      }
    }

    // Fallback to legacy JSON format for backward compatibility
    try {
      const parsed = JSON.parse(cleaned);
      this.debugLog('[ScannerEventService] parsed JSON:', parsed);

      // Validate this is a package QR code
      this.debugLog('[ScannerEventService] Validating parsed JSON as package QR code');
      this.debugLog('[ScannerEventService] parsed.type:', parsed.type);
      this.debugLog('[ScannerEventService] parsed.id:', parsed.id);
      this.debugLog('[ScannerEventService] parsed.dimensions:', parsed.dimensions);

      if (parsed.type === 'package' && parsed.id && parsed.dimensions) {
        const id = parseInt(parsed.id, 10);
        this.debugLog('[ScannerEventService] Parsed ID as integer:', id);

        // Validate reasonable ID range and required fields
        if (id > 0 && id < 999999999 &&
            parsed.dimensions.length &&
            parsed.dimensions.width &&
            parsed.dimensions.height) {
          this.debugLog('[ScannerEventService] Package QR validation SUCCESS, returning data');
          return {
            id: id,
            data: parsed
          };
        } else {
          this.debugLog('[ScannerEventService] Package QR validation FAILED - invalid ID or dimensions');
        }
      } else {
        this.debugLog('[ScannerEventService] Package QR validation FAILED - missing required fields');
      }
    } catch (error) {
      this.debugLog('[ScannerEventService] JSON parse error:', error);
      // Not JSON or malformed, not a package QR code
    }

    this.debugLog('[ScannerEventService] extractPackageQRData returning null');
    return null;
  }

  private parseScannerFriendlyQR(qrData: string): { id: number, data: any } | null {
    try {
      // Format: PKG{version}L{length}W{width}H{height}T{timestamp}C{checksum}
      // Example: PKG10L12.5W8.0H4.0T1758387036C34FEE2A2

      this.debugLog('[ScannerEventService] Parsing scanner-friendly QR:', qrData);

      if (!qrData.startsWith('PKG')) {
        this.debugLog('[ScannerEventService] Not a PKG format QR code');
        return null;
      }

      let data = qrData.substring(3); // Remove "PKG" prefix

      // Extract version (assumes 2-digit version like "10" for v1.0)
      if (data.length < 2) {
        this.debugLog('[ScannerEventService] Data too short for version');
        return null;
      }

      const versionStr = data.substring(0, 2);
      const versionInt = parseInt(versionStr, 10);
      if (isNaN(versionInt)) {
        this.debugLog('[ScannerEventService] Invalid version format');
        return null;
      }

      const version = `${Math.floor(versionInt / 10)}.${versionInt % 10}`;
      data = data.substring(2);

      this.debugLog('[ScannerEventService] Parsed version:', version);
      this.debugLog('[ScannerEventService] Remaining data:', data);

      // Extract dimensions using field markers
      const dimensions = { length: 0, width: 0, height: 0 };
      let timestamp: number | null = null;
      let checksum: string | null = null;

      // Extract Length (L{number})
      const lIndex = data.indexOf('L');
      const wIndex = data.indexOf('W');
      if (lIndex >= 0 && wIndex > lIndex) {
        const lengthStr = data.substring(lIndex + 1, wIndex);
        let length = parseFloat(lengthStr);
        if (!isNaN(length)) {
          // Check if dimension appears to be compressed (multiplied by 10)
          // Heuristic: if dimension > 100, it's likely compressed decimal format
          if (length > 100) {
            length = length / 10;
            this.debugLog(`[ScannerEventService] Converted compressed length: ${lengthStr} → ${length}`);
          }
          dimensions.length = length;
          this.debugLog('[ScannerEventService] Parsed length:', length);
        }
      }

      // Extract Width (W{number})
      const hIndex = data.indexOf('H');
      if (wIndex >= 0 && hIndex > wIndex) {
        const widthStr = data.substring(wIndex + 1, hIndex);
        let width = parseFloat(widthStr);
        if (!isNaN(width)) {
          // Check if dimension appears to be compressed (multiplied by 10)
          if (width > 100) {
            width = width / 10;
            this.debugLog(`[ScannerEventService] Converted compressed width: ${widthStr} → ${width}`);
          }
          dimensions.width = width;
          this.debugLog('[ScannerEventService] Parsed width:', width);
        }
      }

      // Extract Height (H{number})
      const tIndex = data.indexOf('T');
      if (hIndex >= 0 && tIndex > hIndex) {
        const heightStr = data.substring(hIndex + 1, tIndex);
        let height = parseFloat(heightStr);
        if (!isNaN(height)) {
          // Check if dimension appears to be compressed (multiplied by 10)
          if (height > 100) {
            height = height / 10;
            this.debugLog(`[ScannerEventService] Converted compressed height: ${heightStr} → ${height}`);
          }
          dimensions.height = height;
          this.debugLog('[ScannerEventService] Parsed height:', height);
        }
      }

      // Extract Timestamp (T{number})
      const cIndex = data.indexOf('C');
      if (tIndex >= 0 && cIndex > tIndex) {
        const timestampStr = data.substring(tIndex + 1, cIndex);
        const ts = parseInt(timestampStr, 10);
        if (!isNaN(ts)) {
          timestamp = ts;
          this.debugLog('[ScannerEventService] Parsed timestamp:', ts);
        }
      }

      // Extract Checksum (C{string})
      if (cIndex >= 0) {
        checksum = data.substring(cIndex + 1);
        this.debugLog('[ScannerEventService] Parsed checksum:', checksum);
      }

      // Validate we have valid dimensions
      if (dimensions.length > 0 && dimensions.width > 0 && dimensions.height > 0) {
        this.debugLog('[ScannerEventService] Scanner-friendly QR parsed successfully:', dimensions);

        // Create compatible data structure
        const packageData = {
          type: 'package',
          version: version,
          id: 1, // Use generic ID for scanner-friendly format
          name: 'Package',
          dimensions: dimensions,
          weight: null,
          timestamp: timestamp || Math.floor(Date.now() / 1000),
          checksum: checksum
        };

        return {
          id: 1, // Use generic ID for scanner-friendly format
          data: packageData
        };
      } else {
        this.debugLog('[ScannerEventService] Invalid dimensions in scanner-friendly QR:', dimensions);
        return null;
      }
    } catch (error) {
      this.debugLog('[ScannerEventService] Error parsing scanner-friendly QR:', error);
      return null;
    }
  }

  private extractPickTicketId(scanData: any): number | null {
    // Ensure scanData is a string
    const scanString = typeof scanData === 'string' ? scanData :
                      (scanData && typeof scanData.toString === 'function' ? scanData.toString() : '');

    if (!scanString) {
      return null;
    }

    // Remove any asterisks (CODE128 formatting)
    const cleaned = scanString.replace(/\*/g, '').trim();

    // Pattern 1: Just a number (most common for pick tickets)
    if (/^\d+$/.test(cleaned)) {
      const id = parseInt(cleaned, 10);
      // Validate reasonable ID range for pick tickets
      if (id > 0 && id < 999999999) {
        return id;
      }
    }

    // Pattern 2: PT prefix (e.g., "PT123456")
    const ptMatch = cleaned.match(/^PT(\d+)$/i);
    if (ptMatch) {
      const id = parseInt(ptMatch[1], 10);
      if (id > 0 && id < 999999999) {
        return id;
      }
    }

    // Pattern 3: JSON format (for future QR codes)
    try {
      const parsed = JSON.parse(cleaned);
      if (parsed.type === 'pickTicket' && parsed.id) {
        return parseInt(parsed.id, 10);
      }
    } catch {
      // Not JSON, continue
    }

    return null;
  }

  private async handlePackageQRScan(packageId: number, packageData: any, rawData: string) {
    this.debugLog('[ScannerEventService] *** PACKAGE QR SCAN DETECTED ***');
    this.debugLog('[ScannerEventService] packageId:', packageId);
    this.debugLog('[ScannerEventService] packageData:', packageData);
    this.debugLog('[ScannerEventService] rawData:', rawData);

    // Emit global package scan event
    const eventData = {
      packageId,
      packageData,
      rawData,
      timestamp: Date.now()
    };

    this.debugLog('[ScannerEventService] *** EMITTING package-qr-scanned EVENT ***');
    this.debugLog('[ScannerEventService] Event data:', eventData);

    this.emit('package-qr-scanned', eventData);

    // Show package-specific visual and audio feedback
    this.playSuccessSound();
    this.showPackageScanAnimation(packageData.name);

    // Show success notification
    const preview = packageData.name ? packageData.name : `Package #${packageId}`;
    Vue.prototype.$toast?.success(`Package Scanned: ${preview}`);
  }

  private async handlePickTicketScan(pickTicketId: number, rawData: string) {
    this.debugLog('[ScannerEventService] Pick ticket scanned:', pickTicketId);

    // Emit pick ticket scanned event
    this.emit('pick-ticket-scanned', {
      pickTicketId,
      rawData,
      timestamp: Date.now()
    });

    // Check if user is logged in
    if (!store.getters.isLoggedIn) {
      Vue.prototype.$toast?.warning('Please log in to create shipping labels');
      return;
    }

    // If a shipping label dialog is already open, ignore the scan entirely.
    // The user must manually close the current dialog before scanning a new pick ticket.
    if (store.getters['shippingDialog/isDialogOpen']) {
      return;
    }

    // Play success sound
    this.playSuccessSound();

    // Show scanning animation
    this.showScanAnimation();

    // Open shipping dialog via Vuex
    try {
      await store.dispatch('shippingDialog/openShippingDialog', pickTicketId);
    } catch (error: any) {
      this.debugError('[ScannerEventService] Failed to open shipping dialog:', error);
      Vue.prototype.$toast?.error(error.message || 'Failed to open label generator');
    }
  }

  private playSuccessSound() {
    try {
      // Check if audio feedback is enabled
      const audioEnabled = localStorage.getItem('scanner_audio_feedback') !== 'false';
      if (!audioEnabled) return;
      
      // Simple beep sound using Web Audio API
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContext) return;
      
      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 880; // A5 note
      gainNode.gain.value = 0.1; // Low volume
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1); // 100ms beep
    } catch (error) {
      this.debugError('Could not play sound:', error);
    }
  }

  private showScanAnimation() {
    // Check if visual feedback is enabled
    const visualEnabled = localStorage.getItem('scanner_visual_feedback') !== 'false';
    if (!visualEnabled) return;

    // Create and inject scan animation overlay
    const overlay = document.createElement('div');
    overlay.className = 'scan-success-overlay';
    overlay.innerHTML = `
      <div class="scan-success-animation">
        <svg width="100" height="100" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="#4caf50" stroke-width="3">
            <animate attributeName="r" from="10" to="45" dur="0.5s" />
            <animate attributeName="opacity" from="1" to="0" dur="0.5s" />
          </circle>
          <path d="M30 50 L45 65 L70 35" fill="none" stroke="#4caf50" stroke-width="4" stroke-linecap="round">
            <animate attributeName="stroke-dasharray" from="0 100" to="100 0" dur="0.3s" />
          </path>
        </svg>
      </div>
    `;

    document.body.appendChild(overlay);

    setTimeout(() => {
      overlay.classList.add('fade-out');
      setTimeout(() => {
        if (document.body.contains(overlay)) {
          document.body.removeChild(overlay);
        }
      }, 300);
    }, 700);
  }

  private showPackageScanAnimation(packageName?: string) {
    // Check if visual feedback is enabled
    const visualEnabled = localStorage.getItem('scanner_visual_feedback') !== 'false';
    if (!visualEnabled) return;

    // Create and inject package scan animation overlay
    const overlay = document.createElement('div');
    overlay.className = 'package-scan-success-overlay';
    overlay.innerHTML = `
      <div class="package-scan-animation">
        <svg width="120" height="120" viewBox="0 0 120 120">
          <!-- Package icon -->
          <rect x="30" y="40" width="60" height="40" fill="none" stroke="#4caf50" stroke-width="3" rx="4">
            <animate attributeName="opacity" from="0" to="1" dur="0.3s" />
          </rect>
          <!-- Package flap -->
          <path d="M30 40 L60 25 L90 40" fill="none" stroke="#4caf50" stroke-width="2">
            <animate attributeName="opacity" from="0" to="1" dur="0.3s" begin="0.1s" />
          </path>
          <!-- Success checkmark -->
          <path d="M40 65 L55 75 L80 50" fill="none" stroke="#4caf50" stroke-width="4" stroke-linecap="round">
            <animate attributeName="stroke-dasharray" from="0 100" to="100 0" dur="0.4s" begin="0.3s" />
          </path>
          <!-- Package label -->
          <text x="60" y="100" text-anchor="middle" fill="#4caf50" font-size="10" font-weight="bold">
            Package Scanned
          </text>
          ${packageName ? `<text x="60" y="112" text-anchor="middle" fill="#4caf50" font-size="8">${packageName.substring(0, 15)}</text>` : ''}
        </svg>
      </div>
    `;

    // Add styles for the animation
    if (!document.querySelector('#package-scan-styles')) {
      const style = document.createElement('style');
      style.id = 'package-scan-styles';
      style.textContent = `
        .package-scan-success-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.3);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 10000;
          pointer-events: none;
        }
        .package-scan-animation {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
          animation: packageScanPop 0.6s ease-out;
        }
        @keyframes packageScanPop {
          0% { transform: scale(0.8) translateY(20px); opacity: 0; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        .package-scan-success-overlay.fade-out {
          animation: fadeOut 0.3s ease-out forwards;
        }
        @keyframes fadeOut {
          to { opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(overlay);

    setTimeout(() => {
      overlay.classList.add('fade-out');
      setTimeout(() => {
        if (document.body.contains(overlay)) {
          document.body.removeChild(overlay);
        }
      }, 300);
    }, 1000);
  }

  async discoverScanners() {
    return this.hardwareService.discoverScanners();
  }

  async connectToScanner(scannerId: string) {
    return this.hardwareService.connectToScanner(scannerId);
  }

  async disconnectScanner(scannerId: string) {
    return this.hardwareService.disconnectScanner(scannerId);
  }

  getConnectedScanners() {
    // This would need to be implemented in the hardware service
    // For now, return empty array as scanners are handled internally
    return [];
  }

  destroy() {
    if (this.hardwareService) {
      this.hardwareService.removeAllListeners('scan');
      this.hardwareService.removeAllListeners('scanner.barcode');
      this.hardwareService.removeAllListeners('scanner-data');
    }

    // CRITICAL FIX: Clean up KeyboardWedgeScanner events
    if (this.keyboardWedgeScanner) {
      this.keyboardWedgeScanner.removeAllListeners('scan');
      this.debugLog('[ScannerEventService] ✅ Cleaned up KeyboardWedgeScanner event listeners');
    }

    this.isListening = false;
    this.scanHistory.clear();
  }
}

// Singleton instance
let scannerService: ScannerEventService | null = null;

export function getScannerEventService(): ScannerEventService {
  if (!scannerService) {
    scannerService = new ScannerEventService();
  }
  return scannerService;
}